import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg, fmtDate, money } from "@/lib/api";
import { BureauChip } from "@/components/StatusChip";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, FileText, Lock, Plus, Mail, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function DiyHome() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ bureau: "experian", template_id: "round1", item_ids: [] });
  const [busyId, setBusyId] = useState("");

  const load = () => api.get("/diy/overview").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  if (!data) return <div className="py-20 text-center text-slate-400 text-sm">Loading your letters…</div>;

  const available = data.items.filter((i) => i.bureau === form.bureau && ["confirmed", "flagged"].includes(i.status));
  const infoComplete = !!data.client.address?.street;

  const createLetter = async () => {
    if (form.item_ids.length === 0) return toast.error("Select at least one item");
    try {
      await api.post("/diy/letters", form);
      toast.success("Letter generated — pay to unlock your PDF");
      setOpen(false);
      setForm({ bureau: "experian", template_id: "round1", item_ids: [] });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const pay = async (letter) => {
    if (!letter.invoice) return toast.error("No invoice found for this letter");
    setBusyId(letter.id);
    try {
      const order = await api.post("/diy/billing/create-order", { invoice_id: letter.invoice.id });
      await api.post("/diy/billing/capture", { invoice_id: letter.invoice.id, order_id: order.data.order_id });
      toast.success("Payment complete — your letter is unlocked");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusyId(""); }
  };

  const copyText = async (t) => {
    try {
      await navigator.clipboard.writeText(t || "");
      toast.success("Letter text copied — paste it into the bureau portal");
    } catch {
      toast.error("Copy failed");
    }
  };

  const download = async (letter) => {    try {
      const r = await api.get(`/diy/letters/${letter.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dispute-letter-${letter.bureau}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6" data-testid="diy-home">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">My dispute letters</h1>
          <p className="text-slate-600 mt-1">Generate professional FCRA dispute letters for {money(data.letter_price)} each. Download, print, and mail them yourself via USPS Certified Mail.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-letter-btn" className="bg-[#0B2545] hover:bg-[#134074] text-white"><Plus className="h-4 w-4 mr-2" /> New letter</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">Generate a dispute letter</DialogTitle></DialogHeader>
            {!infoComplete && (
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                Complete <Link to="/diy/info" className="font-semibold underline">My Info</Link> first — bureaus verify your identity against your address and DOB.
              </div>
            )}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Bureau</Label>
                  <Select value={form.bureau} onValueChange={(v) => setForm({ ...form, bureau: v, item_ids: [] })}>
                    <SelectTrigger className="mt-1" data-testid="diy-bureau-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="experian">Experian</SelectItem>
                      <SelectItem value="transunion">TransUnion</SelectItem>
                      <SelectItem value="equifax">Equifax</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Letter type</Label>
                  <Select value={form.template_id} onValueChange={(v) => setForm({ ...form, template_id: v })}>
                    <SelectTrigger className="mt-1" data-testid="diy-template-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{data.templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Items to dispute ({available.length} on your list for this bureau)</Label>
                <div className="mt-2 max-h-52 overflow-y-auto space-y-2 rounded-md border border-slate-200 p-3">
                  {available.length === 0 && <div className="text-xs text-slate-400">No items for this bureau yet — add them under <b>My Items</b>.</div>}
                  {available.map((i) => (
                    <label key={i.id} className="flex items-start gap-2 text-sm text-slate-700">
                      <Checkbox data-testid="diy-item-checkbox"
                        checked={form.item_ids.includes(i.id)}
                        onCheckedChange={(v) => setForm({ ...form, item_ids: v ? [...form.item_ids, i.id] : form.item_ids.filter((x) => x !== i.id) })} />
                      <span>{i.creditor} <span className="text-xs text-slate-400">· {i.type.replace("_", " ")} {i.balance ? `· ${money(i.balance)}` : ""}</span></span>
                    </label>
                  ))}
                </div>
              </div>
              <Button onClick={createLetter} disabled={!infoComplete} data-testid="diy-generate-letter-btn" className="w-full bg-[#0B2545] hover:bg-[#134074] text-white">
                Generate letter ({money(data.letter_price)})
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600 flex gap-3">
        <Mail className="h-5 w-5 text-[#0B2545] shrink-0 mt-0.5" />
        <p><b>How DIY works:</b> 1) Add your negative items (or upload your report and let AI find them). 2) Generate a letter. 3) Pay to unlock the PDF. 4) Print it and mail it to the bureau via <b>USPS Certified Mail with Return Receipt</b> — the bureau address is included with every letter. They have 30 days to respond. <span className="text-slate-500"><b>Note:</b> all DIY letter sales are final — no refunds.</span></p>
      </div>

      <div className="space-y-3">
        {data.letters.map((l) => (
          <div key={l.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`diy-letter-${l.id.slice(0, 8)}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-slate-400" />
                  <span className="font-medium text-slate-900">{l.template_name}</span>
                  <BureauChip bureau={l.bureau} />
                </div>
                <div className="text-xs text-slate-400 mt-1">{fmtDate(l.created_at)} · {l.item_ids.length} item{l.item_ids.length === 1 ? "" : "s"} · {(l.items || []).map((i) => i.creditor).join(", ")}</div>
              </div>
              <div>
                {l.status === "ready" ? (
                  <Button onClick={() => download(l)} data-testid={`diy-download-${l.id.slice(0, 8)}`} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <Download className="h-4 w-4 mr-2" /> Download PDF
                  </Button>
                ) : (
                  <Button onClick={() => pay(l)} disabled={busyId === l.id} data-testid={`diy-pay-${l.id.slice(0, 8)}`} className="bg-[#FFC439] hover:bg-[#f2b621] text-slate-900 font-semibold">
                    <Lock className="h-4 w-4 mr-2" /> {busyId === l.id ? "Processing…" : `Pay ${money(l.invoice?.amount ?? data.letter_price)} to unlock`}
                  </Button>
                )}
              </div>
            </div>
            {l.status === "ready" && (
              <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700">
                <div className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500 mb-2">Mail certified to</div>
                <div className="whitespace-pre-line font-medium">{l.bureau_address}</div>
                <p className="text-xs text-slate-500 mt-2">At any USPS counter ask for <b>Certified Mail with Return Receipt</b> (about $8–10). Keep the tracking number — it starts the bureau's 30-day legal clock. Attach a copy of your ID and a proof of address.</p>
              </div>
            )}
            {l.status === "ready" && (
              <div className="mt-3 rounded-lg bg-sky-50 border border-sky-200 p-4 text-sm text-sky-900" data-testid={`diy-online-${l.id.slice(0, 8)}`}>
                <div className="text-xs font-mono uppercase tracking-[0.18em] text-sky-700 mb-1">Prefer online? Submit free in 5 minutes</div>
                <p className="text-xs text-sky-800 leading-relaxed mb-2">The bureaus' online dispute portals are free — but only you can submit (third parties can't file online for you). Copy your letter text and paste it into the portal.</p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => copyText(l.letter_text)} data-testid={`diy-copy-${l.id.slice(0, 8)}`}><Copy className="h-3.5 w-3.5 mr-1.5" /> Copy letter text</Button>
                  {l.portal_url && (
                    <a href={l.portal_url} target="_blank" rel="noreferrer" data-testid={`diy-portal-${l.id.slice(0, 8)}`}
                      className="inline-flex items-center gap-1.5 rounded-md border border-sky-300 bg-white px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 transition-colors duration-150">
                      <ExternalLink className="h-3.5 w-3.5" /> Open {data.bureaus[l.bureau]} portal
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
        {data.letters.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-400 text-sm">
            No letters yet. Click <b>New letter</b> to generate your first dispute.
          </div>
        )}
      </div>
    </div>
  );
}
